<template>
  <BaseModal
    :model-value="signInModal.isOpen"
    :title="$t('auth.signInTitle')"
    hide-footer
    @update:model-value="signInModal.close()"
  >
    <div v-if="!emailMode" class="signin-options">
      <button
        v-for="provider in orderedProviders"
        :key="provider.id"
        type="button"
        class="signin-btn"
        :class="provider.id === 'apple.com' ? 'signin-btn--apple' : 'signin-btn--google'"
        :data-testid="provider.id === 'apple.com' ? 'signin-apple' : 'signin-google'"
        :disabled="busy"
        @click="choose(provider.id)"
      >
        <span class="signin-btn-icon" v-html="provider.icon"></span>
        {{ $t(provider.labelKey) }}
      </button>
      <!-- Reviewer path: Play review devices have no Google account, so Google
           requires a standalone email/password login reachable from the normal
           sign-in screen. Android-only; muted on purpose for everyone else. -->
      <button
        v-if="isNativeAndroid"
        type="button"
        class="signin-email-link"
        data-testid="signin-email-link"
        :disabled="busy"
        @click="emailMode = true"
      >
        {{ $t("auth.signInWithEmail") }}
      </button>
    </div>
    <!-- Stays open during submit (unlike choose(), which closes before the
         OAuth takeover) so the inline error is actually visible. -->
    <form
      v-else
      class="signin-email-form"
      data-testid="signin-email-form"
      novalidate
      @submit.prevent="submitEmail"
    >
      <input
        v-model="email"
        type="email"
        autocomplete="email"
        autocapitalize="off"
        spellcheck="false"
        class="signin-input"
        data-testid="signin-email-input"
        :placeholder="$t('auth.email')"
        :aria-label="$t('auth.email')"
        :disabled="busy"
      />
      <input
        v-model="password"
        type="password"
        autocomplete="current-password"
        class="signin-input"
        data-testid="signin-password-input"
        :placeholder="$t('auth.password')"
        :aria-label="$t('auth.password')"
        :disabled="busy"
      />
      <p
        v-if="errorKey"
        class="signin-email-error"
        data-testid="signin-email-error"
        role="alert"
      >
        {{ $t("auth." + errorKey) }}
      </p>
      <button
        type="submit"
        class="signin-btn signin-btn--google"
        data-testid="signin-email-submit"
        :disabled="!canSubmit"
      >
        {{ $t("auth.signIn") }}
      </button>
      <!-- Deliberately NOT :disabled="busy". Back doubles as the escape hatch
           for a request that never settles; disabling it would leave closing
           the whole modal as the only way out of a hung submit. -->
      <button
        type="button"
        class="signin-email-link"
        data-testid="signin-email-back"
        @click="exitEmailMode"
      >
        {{ $t("auth.backToOptions") }}
      </button>
    </form>
  </BaseModal>
</template>

<script>
import BaseModal from "@/components/Modal.vue";
import { signInModal } from "@/store/signInModal.js";
import { authUtils } from "@/scripts/authUtils.js";

// Inline SVGs keep the modal dependency-free. Apple mark per HIG; Google "G"
// per Google brand guidelines.
const APPLE_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M16.36 12.94c.03 3.2 2.8 4.26 2.83 4.28-.02.07-.44 1.51-1.46 3-.88 1.28-1.79 2.56-3.23 2.59-1.41.03-1.87-.84-3.48-.84-1.62 0-2.12.81-3.46.87-1.39.05-2.44-1.39-3.33-2.67C2.4 17.55 1.02 12.72 2.89 9.5c.93-1.6 2.58-2.61 4.38-2.64 1.36-.03 2.65.92 3.48.92.83 0 2.4-1.14 4.04-.97.69.03 2.62.28 3.86 2.09-.1.06-2.31 1.35-2.29 4.04zM13.7 4.06c.74-.9 1.24-2.14 1.1-3.38-1.07.04-2.36.71-3.12 1.6-.69.79-1.29 2.06-1.13 3.27 1.19.09 2.41-.6 3.15-1.49z"/></svg>`;
const GOOGLE_ICON = `<svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>`;

export default {
  name: "SignInModal",
  components: { BaseModal },
  data() {
    return {
      signInModal,
      busy: false,
      emailMode: false,
      email: "",
      password: "",
      // null | "invalidCredentials" | "networkError" — resolved to an i18n
      // key as `auth.${errorKey}` in the template.
      errorKey: null,
      // Bumped every time the email form is torn down. A submit that settles
      // after its token was bumped belongs to an abandoned modal session and
      // must not touch current state (see submitEmail).
      submitSession: 0,
    };
  },
  watch: {
    // BaseModal can be dismissed any way (scrim, X); reset so reopening
    // always starts at the provider list with blank fields.
    "signInModal.isOpen"(open) {
      if (!open) this.exitEmailMode();
    },
  },
  computed: {
    isNativeIos() {
      return (
        window.Capacitor?.isNativePlatform?.() === true &&
        window.Capacitor?.getPlatform?.() === "ios"
      );
    },
    isNativeAndroid() {
      return (
        window.Capacitor?.isNativePlatform?.() === true &&
        window.Capacitor?.getPlatform?.() === "android"
      );
    },
    orderedProviders() {
      const google = {
        id: "google.com",
        labelKey: "auth.continueWithGoogle",
        icon: GOOGLE_ICON,
      };
      const apple = {
        id: "apple.com",
        labelKey: "auth.continueWithApple",
        icon: APPLE_ICON,
      };
      // Apple-on-Android needs extra Services ID redirect config in the
      // native plugin; hidden for v1 (guideline 4.8 only governs the iOS app).
      if (this.isNativeAndroid) return [google];
      // HIG: Sign in with Apple must be at least as prominent as other options
      // in the iOS app, so it goes first there.
      return this.isNativeIos ? [apple, google] : [google, apple];
    },
    canSubmit() {
      return this.email.trim() !== "" && this.password !== "" && !this.busy;
    },
  },
  methods: {
    async choose(providerId) {
      if (this.busy) return;
      this.busy = true;
      // Close before starting: popup/redirect flows take over the viewport and
      // the native sheet renders above the webview anyway.
      signInModal.close();
      try {
        await authUtils.signInWithProvider(providerId);
      } finally {
        this.busy = false;
      }
    },
    async submitEmail() {
      if (!this.canSubmit) return;
      // Capture the session so a request the user walked away from cannot
      // close a freshly reopened modal or inject a stale error into it.
      const session = this.submitSession;
      this.busy = true;
      this.errorKey = null;
      try {
        await authUtils.signInWithEmail(this.email, this.password);
        // Unconditional: a success authenticates the user no matter which
        // modal session started it, and signInWithEmail has already navigated
        // by this point. Gating this on the session would leave the sign-in
        // surface hanging over the dashboard the user was just sent to.
        signInModal.close();
      } catch (error) {
        if (session !== this.submitSession) return;
        // Two buckets only. Enumeration protection collapses the legacy
        // per-code errors into auth/invalid-credential, so never match those.
        this.errorKey =
          error?.code === "auth/network-request-failed"
            ? "networkError"
            : "invalidCredentials";
      } finally {
        // Only the session that still owns the form may release the lock; a
        // stale request must not re-enable a form a newer submit is using.
        if (session === this.submitSession) this.busy = false;
      }
    },
    exitEmailMode() {
      // Abandon any in-flight submit. Clearing busy here is what stops a hung
      // request from leaving every sign-in button disabled forever; the
      // session bump is what keeps that abandoned request from mutating the
      // new session's state when it eventually settles.
      this.submitSession += 1;
      // Only release the lock if the email form owned it. choose() closes the
      // modal while its OAuth request is still pending, which fires the reset
      // watcher too — clearing busy there would re-enable the provider
      // buttons mid-flight and allow a concurrent provider sign-in.
      if (this.emailMode) this.busy = false;
      this.emailMode = false;
      this.email = "";
      this.password = "";
      this.errorKey = null;
    },
  },
};
</script>

<style scoped>
.signin-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0 16px;
}

.signin-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  border-radius: var(--al-radius-md, 12px);
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color var(--al-dur-short, 200ms) var(--al-ease-move, ease),
    border-color var(--al-dur-short, 200ms) var(--al-ease-move, ease);
}

/* Apple's own brand guideline is the black "Sign in with Apple" button in
   both themes, so this one is intentionally theme-invariant. */
.signin-btn--apple {
  background: #000;
  color: #fff;
}

.signin-btn--apple:hover:not(:disabled) {
  background: #1a1a1a;
}

/* Google button uses the app's role tokens so it flips with the rest of the
   dark-mode system (DESIGN.md: new UI must use role tokens for anything that
   changes between themes). */
.signin-btn--google {
  background: var(--al-surface-raised);
  color: var(--al-text);
  border-color: var(--al-border);
}

.signin-btn--google:hover:not(:disabled) {
  background: var(--al-surface-sunken);
}

.signin-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.signin-btn-icon {
  display: inline-flex;
  align-items: center;
}

.signin-email-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0 16px;
}

.signin-input {
  min-height: 48px;
  padding: 0 14px;
  border-radius: var(--al-radius-md, 12px);
  border: 1px solid var(--al-border);
  background: var(--al-surface-raised);
  color: var(--al-text);
  font-family: inherit;
  font-size: 16px;
}

.signin-input:disabled {
  opacity: 0.6;
}

.signin-email-link {
  background: none;
  border: none;
  padding: 4px;
  align-self: center;
  font-family: inherit;
  font-size: 14px;
  color: var(--al-text-muted);
  cursor: pointer;
}

.signin-email-link:disabled {
  opacity: 0.6;
  cursor: default;
}

/* --al-danger-text (not --al-danger) is the role token for danger-coloured
   TEXT: it is the only one with a dark-mode override tuned for AA on a dark
   surface. --al-danger is the fill/accent value and is unthemed, so bare red
   text on --al-ink-800 would fail contrast. */
.signin-email-error {
  margin: 0;
  font-size: 14px;
  color: var(--al-danger-text);
}
</style>
